import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { matrixAPI, chatAPI, subscriptionAPI, calendarAPI, statusAPI } from '../utils/api';
import { format, addDays } from 'date-fns';
import { getLocalMatrixHistory } from '../utils/matrixHistoryLocal';
import DateSelect from '../components/DateSelect';
import './DashboardPage.scss';

interface MatrixCalculation {
  id: number | string;
  name: string;
  birth_date: string;
  matrix_data?: any;
  created_at: string;
}

interface ChatSession {
  id: number;
  created_at: string;
  updated_at: string;
}

interface Subscription {
  id: number;
  plan_type: string;
  start_date: string;
  end_date: string;
  messages_limit: number;
  messages_used: number;
  auto_renew: boolean;
  is_active: boolean;
}

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [calculations, setCalculations] = useState<MatrixCalculation[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [newCalcName, setNewCalcName] = useState('');
  const [newCalcDate, setNewCalcDate] = useState('');
  const [todayScore, setTodayScore] = useState<{
    hasData: boolean;
    score: number | null;
    level: string | null;
    recommendation: string | null;
  } | null>(null);
  const [weekScores, setWeekScores] = useState<Array<{ date: string; score: number; level: string }>>([]);
  const [virtualPayment, setVirtualPayment] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    loadData();
    statusAPI.get()
      .then(({ data }) => setVirtualPayment(!!data?.virtualPayment))
      .catch(() => setVirtualPayment(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, i18n.language]);

  const loadData = async () => {
    try {
      const [calcRes, sessionsRes, subRes, todayRes, weekRes] = await Promise.all([
        matrixAPI.getHistory().catch(() => ({ data: { calculations: [] } })),
        chatAPI.getSessions().catch(() => ({ data: { sessions: [] } })),
        subscriptionAPI.getSubscription().catch(() => ({ data: { subscription: null } })),
        calendarAPI.getToday(i18n.language).catch(() => ({ data: { hasData: false } })),
        calendarAPI.getScores(format(new Date(), 'yyyy-MM-dd'), format(addDays(new Date(), 6), 'yyyy-MM-dd'), i18n.language).catch(() => ({ data: { scores: [] } })),
      ]);

      setTodayScore(todayRes.data);
      setWeekScores((weekRes.data.scores || []).map((s: any) => ({ date: s.date, score: s.score, level: s.level })));

      const apiCalcs = (calcRes.data.calculations || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        birth_date: c.birth_date,
        matrix_data: c.matrix_data,
        created_at: c.created_at,
      }));
      const localCalcs = getLocalMatrixHistory().map((c) => ({
        id: c.id,
        name: c.name,
        birth_date: c.birth_date,
        created_at: c.created_at,
      }));
      setCalculations([...apiCalcs, ...localCalcs]);
      setSessions(sessionsRes.data.sessions || []);
      setSubscription(subRes.data.subscription || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCalculation = (calc: MatrixCalculation) => {
    navigate('/matrix', {
      state: {
        name: calc.name,
        birthDate: calc.birth_date,
        saved: true,
      },
    });
  };

  const handleContinueSession = (sessionId: number) => {
    navigate('/chat', { state: { sessionId } });
  };

  const handleActivateSubscription = () => {
    navigate('/subscription');
  };

  const handleNewCalculation = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newCalcName.trim();
    const birthDate = newCalcDate;
    if (!name || !birthDate) return;
    navigate('/matrix', { state: { name, birthDate } });
  };

  const handleTestSubscription = async () => {
    try {
      await subscriptionAPI.createSubscription('basic');
      await loadData();
    } catch (error) {
      console.error('Test subscription error:', error);
      alert(t('subscription.errors.purchaseFailed'));
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__container">
          <p>{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__container">
        <header className="dashboard-page__header">
          <h1>{t('dashboard.title')}</h1>
          <div className="dashboard-page__header-actions">
            <button className="btn-secondary" onClick={() => navigate('/chat')}>
              {t('dashboard.newConsultation')}
            </button>
          </div>
        </header>

        <div className="dashboard-page__content">
          {todayScore?.hasData && (
            <>
              <section className="dashboard-section dashboard-section--today" aria-labelledby="dashboard-today-heading">
                <h2 id="dashboard-today-heading">{t('dashboard.today.title')}</h2>
                <div className={`dashboard-today-card dashboard-today-card--${todayScore.level || 'neutral'}`}>
                  <span className="dashboard-today-index">{todayScore.score ?? 0}</span>
                  <span className="dashboard-today-label">
                    {todayScore.level === 'favorable' ? t('dashboard.today.favorable') : todayScore.level === 'risky' ? t('dashboard.today.risky') : t('dashboard.today.neutral')}
                  </span>
                  {todayScore.recommendation && (
                    <p className="dashboard-today-recommendation">{todayScore.recommendation}</p>
                  )}
                </div>
              </section>
              <section className="dashboard-section dashboard-section--mini-calendar" aria-labelledby="dashboard-mini-calendar-heading">
                <h2 id="dashboard-mini-calendar-heading">{t('dashboard.miniCalendar.title')}</h2>
                <div className="dashboard-mini-calendar">
                  {weekScores.map((day) => (
                    <div key={day.date} className={`dashboard-mini-day dashboard-mini-day--${day.level}`} title={`${day.date}: ${day.score}`}>
                      <span className="dashboard-mini-day-date">{format(new Date(day.date), 'd')}</span>
                      <span className="dashboard-mini-day-score">{day.score}</span>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn-secondary" onClick={() => navigate('/calendar')}>
                  {t('dashboard.miniCalendar.fullCalendar')}
                </button>
              </section>
            </>
          )}

          <section id="life-map-section" className="dashboard-section dashboard-section--new-calc">
            <h2>{t('dashboard.newCalculation')}</h2>
            <p className="dashboard-section__desc">{t('dashboard.newCalculationDesc')}</p>
            <form className="new-calculation-form" onSubmit={handleNewCalculation}>
              <input
                type="text"
                className="input-field"
                placeholder={t('home.form.namePlaceholder')}
                value={newCalcName}
                onChange={(e) => setNewCalcName(e.target.value)}
              />
              <DateSelect
                value={newCalcDate}
                onChange={setNewCalcDate}
                maxYear={new Date().getFullYear()}
              />
              <button type="submit" className="btn-primary">
                {t('dashboard.calculate')}
              </button>
            </form>
          </section>

          <section className="dashboard-section">
            <h2>{t('dashboard.history.title')}</h2>
            <div className="history-list">
              {calculations.length === 0 ? (
                <p className="empty-state">{t('dashboard.history.empty')}</p>
              ) : (
                <div className="calculations-grid">
                  {calculations.map((calc) => (
                    <div
                      key={calc.id}
                      className="calculation-card"
                      onClick={() => handleViewCalculation(calc)}
                    >
                      <h3>{calc.name}</h3>
                      <p className="calculation-date">
                        {format(new Date(calc.birth_date), 'dd.MM.yyyy')}
                      </p>
                      <p className="calculation-created">
                        {t('dashboard.history.created')}: {format(new Date(calc.created_at), 'dd.MM.yyyy')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="dashboard-section">
            <h2>{t('dashboard.chatSessions.title')}</h2>
            <div className="sessions-list">
              {sessions.length === 0 ? (
                <p className="empty-state">{t('dashboard.chatSessions.empty')}</p>
              ) : (
                <div className="sessions-grid">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="session-card"
                      onClick={() => handleContinueSession(session.id)}
                    >
                      <p className="session-date">
                        {format(new Date(session.created_at), 'dd.MM.yyyy HH:mm')}
                      </p>
                      <button className="btn-secondary btn-secondary--small">
                        {t('dashboard.chatSessions.continue')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="dashboard-section">
            <h2>{t('dashboard.subscription.title')}</h2>
            <div className="subscription-info">
                  {subscription && subscription.is_active ? (
                <>
                  {(() => {
                    const daysLeft = Math.ceil(
                      (new Date(subscription.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                    );
                    if (daysLeft <= 7 && daysLeft >= 0) {
                      return (
                        <p className="subscription-expiry-banner" role="status">
                          {t('dashboard.subscription.expiresSoon', { days: daysLeft })}
                        </p>
                      );
                    }
                    return null;
                  })()}
                  <p>
                    {t('dashboard.subscription.status')}: <span className="status-active">{t('dashboard.subscription.active')}</span>
                  </p>
                  <p>
                    {t('dashboard.subscription.plan')}: {t(`dashboard.subscription.plans.${subscription.plan_type}`)}
                  </p>
                  <p>
                    {t('dashboard.subscription.expires')}: {format(new Date(subscription.end_date), 'dd.MM.yyyy HH:mm')}
                  </p>
                  <p>
                    {t('dashboard.subscription.messagesLeft')}: {subscription.messages_limit - subscription.messages_used}
                  </p>
                  {(subscription.plan_type === 'full2026' || subscription.plan_type === 'premium') && (
                    <button className="btn-secondary" onClick={() => navigate('/certificate')}>
                      {t('dashboard.subscription.certificate')}
                    </button>
                  )}
                  {subscription.auto_renew && (
                    <button className="btn-secondary" onClick={async () => {
                      await subscriptionAPI.cancelSubscription();
                      loadData();
                    }}>
                      {t('dashboard.subscription.cancel')}
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        const { data } = await subscriptionAPI.createBillingPortal();
                        if (data?.url) window.location.href = data.url;
                      } catch (e: any) {
                        alert(e?.response?.data?.error || t('subscription.errors.purchaseFailed'));
                      }
                    }}
                  >
                    {t('dashboard.subscription.manageBilling')}
                  </button>
                </>
              ) : (
                <>
                  <p>
                    {t('dashboard.subscription.status')}: <span className="status-inactive">{t('dashboard.subscription.inactive')}</span>
                  </p>
                  <div className="subscription-actions">
                    <button className="btn-primary" onClick={handleActivateSubscription}>
                      {t('dashboard.subscription.activate')}
                    </button>
                    {virtualPayment && (
                      <button className="btn-secondary" onClick={handleTestSubscription}>
                        {t('dashboard.subscription.testSubscription')}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
